import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import GetCookie from '../../utils/GetCookie';
import ThemeToggle from '../../globalComponents/ThemeToggle';
import Footer from '../../globalComponents/Footer';
import NotificationDropdown from '../../globalComponents/NotificationDropdown';
import AiSearchComponent, { SerachDisplayDiv } from '../../utils/AiSearch';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProfilePopup from '../../globalComponents/ProfilePopup';
import { ProfileCache, NavStateCache } from '../../globalComponents/NavigationCache'; 
import authNavigate from '../../globalComponents/AuthNav';
import { getBrowserTimezone, clearFormatterCache } from '../../utils/dateUtils';
import { useTimezone } from '../../utils/TimezoneContext';

export const AdminTopNav = () => {
  const accessToken = GetCookie('access_token');
  const navigate = useNavigate();
  const user_at = 'admin-console';
  const [response_ai, set_response_ai] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { timezone, setTimezone } = useTimezone();
  
  // Profile states
  const [profileData, setProfileData] = useState(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const profileButtonRef = useRef(null);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfileDetails();
  }, []);

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileButtonRef.current && !profileButtonRef.current.contains(event.target)) {
        setShowProfilePopup(false);
      }
    };
    if (showProfilePopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfilePopup]);

  const fetchProfileDetails = async () => {
    const cachedProfile = ProfileCache.get(user_at);
    if (cachedProfile) {
      setProfileData(cachedProfile);
      return;
    }
    try {
      setIsProfileLoading(true);
      const response = await axios.post(
        '/pceapi/general/profile-details',
        { console_type: user_at },
        { headers: { Authorization: accessToken, 'Content-Type': 'application/json' } }
      );
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        navigate('/admin-login');
        return;
      }
      
      if (data.status === 'failed') {
        setProfileError(data.message || 'Failed to load profile details');
        return;
      }
      
      if (data.status === 'success') {
        const fetchedTz = data.data.timezone;
        
        if (!fetchedTz) {
          const browserTz = getBrowserTimezone();
          data.data.timezone = browserTz;
          
          if (timezone !== browserTz) {
            clearFormatterCache();
            setTimezone(browserTz);
          }
          
          // Fire-and-forget — don't block profile loading
          axios.post(
            '/pceapi/general/update-user-preferences',
            { timezone: browserTz, console_type: user_at },
            { headers: { Authorization: accessToken, 'Content-Type': 'application/json' } }
          ).catch(err => console.error('Auto-save timezone failed:', err));
        } else if (fetchedTz !== timezone) {
          clearFormatterCache();
          setTimezone(fetchedTz);
        }
        
        setProfileData(data.data);
        ProfileCache.set(user_at, data.data);
      }
    } catch (error) {
      console.error('Error fetching profile details:', error);
      setProfileError('Unable to load profile. Please check your connection and try again.');
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleProfileUpdate = (newImageUrl) => {
    const updatedProfile = { 
      ...profileData, 
      profile_image_url: newImageUrl
    };
    setProfileData(updatedProfile);
    ProfileCache.set(user_at, updatedProfile);
  };

  const handleLogout = async () => {
    try {
      const response = await axios.get('/pceapi/auth/admin/logout', {
        headers: { Authorization: accessToken }
      });
      if (response.data.status === 'success') {
        ProfileCache.clear(user_at); // Clear profile cache
        NavStateCache.clear(user_at); // Clear nav state cache on logout
        clearCookie('access_token');
        navigate('/admin-login');
      } else {
        console.error('Logout failed:', response.data.message);
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg fixed-top topnavbar col-lg-10 col-md-9  col-sm-9">
        <div className="container-fluid ps-0">
          <div className="d-flex justify-content-center">
            <AiSearchComponent 
              tok={accessToken} 
              where_user={user_at} 
              setResponse={set_response_ai} 
              isLoading={isLoading} 
              setIsLoading={setIsLoading} 
              setError={setError} 
            />
          </div>
          <div className="">
            <ThemeToggle />
            
            <button className="ev-btn-blue-fill-round ev-btn-med  ev-bold-reg ms-3" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right"></i> Logout 
            </button>
            
            <div className="d-inline-block position-relative ms-3" ref={profileButtonRef}>
              {isProfileLoading ? (
                <button className="ev-btn-lightblue-fill-round nav-round-btn">
                  <i className="bi bi-person-fill"></i>
                </button>
              ) : profileData?.profile_image_url ? (
                <button
                  className=" avatar-tooltip bg-transparent border-0"
                  data-tooltip={profileData?.name || 'Profile'}
                  onClick={() => setShowProfilePopup(!showProfilePopup)}
                >
                  <img 
                    src={profileData.profile_image_url} 
                    className="nav-profile-image"
                     onLoad={(e) => e.target.classList.add('loaded')}
                  />
                </button>
              ) : (
                <button
                  className="ev-btn-lightblue-fill-round nav-round-btn avatar-tooltip"
                  data-tooltip={profileData?.name || 'Profile'}
                  onClick={() => setShowProfilePopup(!showProfilePopup)}
                >
                  <i className="bi bi-person-fill"></i>
                </button>
              )}
              <ProfilePopup
                profileData={profileData}
                consoleType={user_at}
                onClose={() => setShowProfilePopup(false)}
                onProfileUpdate={handleProfileUpdate}
                isOpen={showProfilePopup}
              />
            </div>
            
            <Link 
              to="/admin-user-guide"
              className="ev-btn-lightblue-fill-round nav-round-btn ms-3 avatar-tooltip text-decoration-none d-inline-flex align-items-center justify-content-center"
              data-tooltip="User Help Guide"
            >
              <i className="bi bi-patch-question-fill"></i>
            </Link>
            <NotificationDropdown user_type={"Admin"} />
          </div>
        </div>
      </nav>
      
      {(isLoading || response_ai !== '' && error === null) && (
        <SerachDisplayDiv 
          ai_response={response_ai} 
          set_ai_response={set_response_ai} 
          error={error} 
          isLoading={isLoading} 
          setIsLoading={setIsLoading} 
          setError={setError} 
        />
      )}
      
      {error !== null && error !== '' && (
        <ErrorPopup
          onClose={() => setError(null)}
          message={error}
        />
      )}

      {profileError && (
        <ErrorPopup
          onClose={() => {
            setProfileError(null);
            fetchProfileDetails();
          }}
          message={profileError}
        />
      )}
    </>
  );
};

export const AdminSideNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const consoleType = 'admin-console'; 
  
  
  // But keeping the structure for future expandability
  const getInitialNavState = () => {
    const cachedState = NavStateCache.get(consoleType);
    if (cachedState) {
      return cachedState;
    }
    // Default state - admin currently has no collapsible sections
    return {};
  };

  const [expanded, setExpanded] = useState(getInitialNavState());
  
  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };
  
  const handleClick = (event, path) => {
    event.preventDefault();
    authNavigate(path, 'admin', navigate);
  };

  // If admin gets collapsible sections in future, use this
  const toggleExpand = (branch) => {
    const newExpandedState = {
      ...expanded,
      [branch]: !expanded[branch]
    };
    setExpanded(newExpandedState);
    NavStateCache.set(consoleType, newExpandedState);
  };
  
  return (
    <nav className="col-lg-2 col-md-3  col-sm-3 sidebar">
      <div className="position-sticky">
        <Link to="/" className="text-decoration-none">
          <img src="/media/pharmacoevidence-logo.png" className="logo-color w-45" />
        </Link>
        
        <p className="sidelinkheader mb-1 mt-4">MY MODULES</p>
        
        <Link 
          className={`sidelink ${isActive('/admindashboard')}`} 
          onClick={(e) => handleClick(e, '/admindashboard')} 
        >
          <i className="bi bi-border-inner ev-fs-15"></i> &nbsp; Admin Dashboard
        </Link>
        
        <Link 
          className={`sidelink ${isActive('/admin-manage-permission-sets')}`} 
          onClick={(e) => handleClick(e, '/admin-manage-permission-sets')}   
        >
          <i className="bi bi-sliders ev-fs-15" ></i> &nbsp; Permission Sets
        </Link>
        
        <Link 
          className={`sidelink ${isActive('/admin-manage-iam-users')}`}  
          onClick={(e) => handleClick(e, '/admin-manage-iam-users')}
        > 
          <i className="bi bi-people ev-fs-15" ></i> &nbsp; Manage Users
        </Link>
      </div>
      <Footer />
    </nav>
  );
};

function clearCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}